import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';

import {
  ROLE_TEXT,
  roleColor,
  type RosterEmployee,
  type TeamPreset,
  useEmployeesT,
  useHireHref,
} from './kit';

/**
 * The teams the onboarding proposes for each kind of work, as tabs. Every
 * panel stays in the page (hidden), so all of them are indexed.
 */
export default function EmployeesTeams({
  teams,
  roster,
}: {
  teams: TeamPreset[];
  roster: RosterEmployee[];
}) {
  const { t } = useEmployeesT();
  const [active, setActive] = useState(teams[0]?.id ?? '');
  const hireHref = useHireHref();
  const bySlug = new Map(roster.map((employee) => [employee.slug, employee]));

  if (teams.length === 0) return null;

  return (
    <section id='teams' className='scroll-mt-8 py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('A team for the work you do.')}
          tail={t('Proposed in one click.')}
          lead={t(
            'Tell us what you do when you sign up, and we put together a team for it, led by your Chief of Staff. Here is what we’d suggest.',
          )}
        />

        <div className='mt-12 grid gap-6 lg:grid-cols-12 lg:gap-8'>
          <div
            role='tablist'
            aria-label={t('Kinds of work')}
            aria-orientation='vertical'
            className='-mx-6 flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none] lg:col-span-4 lg:mx-0 lg:flex-col lg:gap-1 lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden'
          >
            {teams.map((team) => {
              const selected = team.id === active;
              return (
                <button
                  key={team.id}
                  type='button'
                  role='tab'
                  id={`team-tab-${team.id}`}
                  aria-selected={selected}
                  aria-controls={`team-panel-${team.id}`}
                  onClick={() => setActive(team.id)}
                  className={clsxm(
                    'flex flex-none items-center gap-3 rounded-2xl px-4 py-2.5 text-start transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600',
                    selected
                      ? clsxm(
                          'bg-white shadow-[0_14px_30px_-22px_rgba(22,12,90,0.6)] dark:bg-white/[0.07]',
                          tone.line,
                        )
                      : 'hover:bg-white/60 dark:hover:bg-white/[0.03]',
                  )}
                >
                  <span className='text-xl' aria-hidden='true'>
                    {team.emoji}
                  </span>
                  <span
                    className={clsxm(
                      'whitespace-nowrap text-[15px] font-semibold',
                      selected ? tone.ink : tone.body,
                    )}
                  >
                    {t(team.label)}
                  </span>
                </button>
              );
            })}
          </div>

          <div className='min-w-0 lg:col-span-8'>
            {teams.map((team) => {
              const members = team.members
                .map((slug) => bySlug.get(slug))
                .filter((member): member is RosterEmployee => Boolean(member));
              return (
                <div
                  key={team.id}
                  role='tabpanel'
                  id={`team-panel-${team.id}`}
                  aria-labelledby={`team-tab-${team.id}`}
                  hidden={team.id !== active}
                >
                  <Stage
                    variant='lavender'
                    halftoneAt='100% 0%'
                    halftoneReach={55}
                    className='p-3 sm:p-6'
                  >
                    <p
                      className={clsxm(
                        'mb-4 flex flex-wrap items-baseline gap-x-2 px-1 text-sm',
                        tone.body,
                      )}
                    >
                      <span className={clsxm('font-semibold', tone.ink)}>
                        {t(team.label)}
                      </span>
                      <span>{t(team.description)}</span>
                    </p>
                    <ul className='grid gap-3 sm:grid-cols-2'>
                      {members.map((member, index) => (
                        <li
                          key={member.slug}
                          className={clsxm(
                            'flex gap-3.5 rounded-2xl bg-white p-3.5 dark:bg-[#16151f]',
                            tone.line,
                            index === 0 && 'sm:col-span-2',
                          )}
                        >
                          <Image
                            src={member.photo}
                            alt=''
                            width={52}
                            height={52}
                            unoptimized
                            className='h-[52px] w-[52px] flex-none rounded-full object-cover'
                          />
                          <span className='min-w-0 flex-1'>
                            <span className='flex flex-wrap items-baseline gap-x-2'>
                              <span
                                className={clsxm(
                                  'text-[15px] font-semibold',
                                  tone.ink,
                                )}
                              >
                                {member.name}
                              </span>
                              <span
                                className={clsxm(
                                  'text-[13px] font-semibold',
                                  ROLE_TEXT,
                                )}
                                style={roleColor(member.color)}
                              >
                                {t(member.role)}
                              </span>
                              {index === 0 ? (
                                <span className='rounded bg-primary-50 px-1.5 text-[10px] font-semibold uppercase leading-4 tracking-wide text-primary-700 dark:bg-primary-400/15 dark:text-primary-200'>
                                  {t('Lead')}
                                </span>
                              ) : null}
                            </span>
                            <span className='mt-2 flex flex-wrap gap-1.5'>
                              {member.capabilities
                                .slice(0, index === 0 ? 3 : 2)
                                .map((capability) => (
                                  <span
                                    key={capability.title}
                                    className={clsxm(
                                      'inline-flex items-center gap-1.5 rounded-full bg-[#f3f3f8] px-2.5 py-1 text-xs font-medium dark:bg-white/[0.06]',
                                      tone.body,
                                    )}
                                  >
                                    <span aria-hidden='true'>
                                      {capability.icon}
                                    </span>
                                    {t(capability.title)}
                                  </span>
                                ))}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className='mt-5 flex flex-col items-start gap-3 px-1 sm:flex-row sm:items-center sm:justify-between'>
                      <p className={clsxm('text-sm', tone.body)}>
                        {t(
                          'Untick anyone you don’t need, and add more roles any time.',
                        )}
                      </p>
                      <Link href={hireHref} className={buttonPrimary}>
                        {t('Hire this team')}
                        <ArrowGlyph />
                      </Link>
                    </div>
                  </Stage>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
